<script setup lang="ts">
  import { computed, onMounted } from 'vue'
  import { useRoute, useRouter } from 'vue-router'
  import {
    useConfigurationStore,
    useDeviceStore,
    useThemeStore,
    useLocaleStore,
    useTrayStore,
    useUpdateStore,
    usePreferenceStore,
    useUserStore,
  } from '@/stores'
  import { useNoticeReport } from '@/composables/useNoticeReport'
  import { storeToRefs } from 'pinia'
  import { preloadWallpaperImage, resolveEarlyPresetWallpaperUrl } from '@/utils/wallpaperPreload'

  

  const localeStore = useLocaleStore()
  localeStore.initLocale()
  const themeStore = useThemeStore()
  const deviceStore = useDeviceStore()
  const trayStore = useTrayStore()
  const configStore = useConfigurationStore()
  const updateStore = useUpdateStore()
  const preferenceStore = usePreferenceStore()
  const userStore = useUserStore()
  const { isLoading } = storeToRefs(userStore)

  const router = useRouter()
  const route = useRoute()

  // 壳层先铺当前预设壁纸：不必等 views/index.vue / DeviceStore
  const earlyWallpaperUrl = resolveEarlyPresetWallpaperUrl(themeStore.isDark)
  const wallpaperReady =
    earlyWallpaperUrl != null ? preloadWallpaperImage(earlyWallpaperUrl) : Promise.resolve()

  // 托盘 / 通知等透明子窗口：不能铺壁纸或页面底色，否则会透出背景
  const isChildWindow = computed(() => route.meta.window !== undefined)

  const shellBackgroundStyle = computed(() => {
    if (isChildWindow.value || !earlyWallpaperUrl) return undefined
    return {
      backgroundImage: `url(${earlyWallpaperUrl})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
    }
  })

  const shellClass = computed(() =>
    isChildWindow.value
      ? 'bg-transparent text-text-important-default'
      : 'min-h-screen bg-page-background text-text-important-default transition-colors duration-300',
  )

  onMounted(async () => {
    await router.isReady()

    // 子窗口无需注册以下内容
    if (isChildWindow.value) {
      return
    }

    // 壁纸优先占带宽；设备通讯本就更慢，manifests 稍后灌入
    await Promise.race([wallpaperReady, new Promise<void>((resolve) => setTimeout(resolve, 2000))])

    await deviceStore.init()
    trayStore.enableDesktopStateSync()
    deviceStore.scan()
    deviceStore.registerCurrentDeviceWatch()
    configStore.registerAppEvents()
    updateStore.registerAvailable()
    updateStore.checkUpdate()
    preferenceStore.initPreference()
    useNoticeReport()
  })
</script>

<template>
  <div v-loading="isLoading" :class="shellClass" :style="shellBackgroundStyle">
    <main>
      
      <RouterView />
    </main>

    <!-- <ConfigProvider :teleport-to="overlayTeleportTo">
      <FirmwareUpgradeProgressDialog />
    </ConfigProvider> -->
  </div>
</template>

<style>
  @supports (background: -webkit-named-image(i)) or (-webkit-touch-callout: none) {
    :root {
      --font-sans:
        'PingFang SC', -apple-system, BlinkMacSystemFont, 'OPPOSans', 'Barlow', ui-sans-serif,
        system-ui, sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol',
        'Noto Color Emoji' !important;
    }
  }

  *:focus-visible {
    outline: none;
    box-shadow: 0 0 0 0 transparent;
  }
</style>
